import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Projects = ({ onEnquire }) => {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then(({ data }) => setProjects(data))
      .catch(() => setProjects([]));
  }, []);

  const featured = (projects || []).filter((p) => p.featured);

  return (
    <section id="projects" className="bg-white py-16 sm:py-24 lg:py-28" data-testid="featured-projects">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <SectionHeading
            label="Featured Projects"
            title="Explore Featured Projects"
            subtitle="Discover projects and developments worth exploring."
          />
          <Reveal delay={0.2}>
            <button
              onClick={() => onEnquire({ interest: "Buy", message: "I'd like to know about your current and upcoming projects." })}
              data-testid="view-all-projects-button"
              className="shrink-0 border border-gold text-navy hover:bg-gold/10 hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-6 py-3.5 text-[14px] font-semibold flex items-center gap-2"
            >
              View All Projects <ArrowRight size={15} />
            </button>
          </Reveal>
        </div>

        {projects === null ? (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-warm rounded-xl border border-line overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-line/50" />
                <div className="p-7 space-y-3">
                  <div className="h-5 bg-line/60 rounded w-2/3" />
                  <div className="h-3.5 bg-line/50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="mt-12 bg-warm rounded-xl border border-line p-12 text-center" data-testid="projects-empty-state">
            <p className="font-display text-xl text-navy">New projects are being added.</p>
            <p className="mt-2 text-sm text-ink-soft">Tell us what you're looking for and we'll share matching projects with you.</p>
            <button
              onClick={() => onEnquire({ interest: "Buy", message: "I'd like to know about your current and upcoming projects." })}
              className="mt-6 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold"
              data-testid="projects-empty-enquire-button"
            >
              Ask About Projects
            </button>
          </div>
        ) : (
          <div className="mt-12 flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-visible lg:pb-0">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1} className="min-w-full sm:min-w-[440px] lg:min-w-0 snap-center">
                <article
                  className="group bg-warm rounded-xl border border-line overflow-hidden hover:shadow-[0_24px_60px_rgba(11,31,51,0.12)] transition-shadow duration-400 h-full"
                  data-testid={`project-card-${i}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-line/40">
                    {p.images?.length > 0 && (
                      <img
                        src={`${API}/uploads/${p.images[0]}`}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    )}
                    <span className="absolute top-4 left-4 bg-white/95 text-navy text-[10px] font-bold tracking-[0.16em] px-3 py-1.5 rounded-md uppercase">
                      {p.status}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-navy">{p.name}</h3>
                    <p className="mt-2 flex items-center gap-1.5 text-[13px] text-ink-soft">
                      <MapPin size={13} className="text-gold" /> {p.location}
                    </p>
                    {p.propertyType && (
                      <p className="mt-2 text-[12px] font-semibold tracking-[0.14em] uppercase text-gold">{p.propertyType}</p>
                    )}
                    {p.shortDesc && <p className="mt-3 text-sm text-ink-soft leading-relaxed">{p.shortDesc}</p>}
                    {(p.startingPrice || p.bhkOptions) && (
                      <p className="mt-3 text-[13px] font-semibold text-navy">
                        {p.startingPrice && <>Starting at <span className="text-gold">{p.startingPrice}</span></>}
                        {p.startingPrice && p.bhkOptions && " · "}
                        {p.bhkOptions}
                      </p>
                    )}
                    <button
                      onClick={() => onEnquire({ interest: "Buy", message: `I'd like to know more about the project "${p.name}" (${p.location}).` })}
                      data-testid={`project-view-button-${i}`}
                      className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-navy border-b-2 border-gold pb-1 hover:gap-3.5 transition-all duration-300"
                    >
                      View Project <ArrowRight size={15} />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
